import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { LineChart } from "@mui/x-charts/LineChart";
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Bookmark,
  Calendar,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Layers,
  Loader2,
  Play,
  Plug,
  Save,
  Search,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";
import AppShell from "../components/AppShell";
import AccountLabel, { AccountIdBadge } from "../components/AccountLabel";
import ExportButton from "../components/ExportButton";
import ApiService from "../services/ApiService";
import IntegrationsService from "../services/IntegrationsService";
import ReportService from "../services/ReportService";
import { cn } from "../components/ui/cn";

const META_ADS_KINDS = ["facebook_ads_account"];
const META_CATALOG_KINDS = ["facebook_catalog"];
const META_NON_ORGANIC_KINDS = [...META_ADS_KINDS, ...META_CATALOG_KINDS];

const METRIC_GROUPS = [
  {
    label: "Meta Ads",
    segment: "Paid Ads",
    providers: ["meta"],
    kinds: META_ADS_KINDS,
    color: "#1877F2",
    metrics: [
      { key: "spend", label: "Spend" },
      { key: "impressions", label: "Impressions" },
      { key: "clicks", label: "Clicks" },
      { key: "ctr", label: "CTR (%)" },
      { key: "cpc", label: "CPC" },
      { key: "conversions", label: "Conversions" },
      { key: "revenue", label: "Revenue" },
      { key: "roas", label: "ROAS" },
    ],
  },
  {
    label: "Meta Catalog",
    segment: "Commerce",
    providers: ["meta"],
    kinds: META_CATALOG_KINDS,
    color: "#F97316",
    metrics: [
      { key: "productCount", label: "Products" },
      { key: "viewContent", label: "View content" },
      { key: "addToCart", label: "Add to cart" },
      { key: "purchase", label: "Purchases" },
      { key: "matchedEvents", label: "Matched events" },
      { key: "unmatchedEvents", label: "Unmatched events" },
      { key: "matchRate", label: "Match rate (%)" },
    ],
  },
  {
    label: "Facebook / Instagram",
    segment: "Organic Social",
    providers: ["meta"],
    excludeKinds: META_NON_ORGANIC_KINDS,
    color: "#E1306C",
    metrics: [
      { key: "reach", label: "Reach" },
      { key: "impressions", label: "Impressions" },
      { key: "engagements", label: "Engagements" },
      { key: "followers", label: "Followers" },
      { key: "profileViews", label: "Profile views (IG)" },
    ],
  },
  {
    label: "Google Analytics 4",
    segment: "Web Traffic",
    providers: ["google"],
    color: "#EA4335",
    metrics: [
      { key: "sessions", label: "Sessions" },
      { key: "users", label: "Users" },
      { key: "newUsers", label: "New users" },
      { key: "pageViews", label: "Page views" },
      { key: "conversions", label: "Conversions" },
    ],
  },
  {
    label: "Google Search Console",
    segment: "Web Traffic",
    providers: ["search_console"],
    color: "#34A853",
    metrics: [
      { key: "clicks", label: "Clicks" },
      { key: "impressions", label: "Impressions" },
      { key: "ctr", label: "CTR (%)" },
      { key: "position", label: "Avg. position" },
    ],
  },
  {
    label: "Google Ads",
    segment: "Paid Ads",
    providers: ["google_ads"],
    color: "#FBBC04",
    metrics: [
      { key: "impressions", label: "Impressions" },
      { key: "clicks", label: "Clicks" },
      { key: "cost", label: "Spend" },
      { key: "conversions", label: "Conversions" },
      { key: "ctr", label: "CTR (%)" },
      { key: "cpc", label: "CPC" },
    ],
  },
  {
    label: "LinkedIn Ads",
    segment: "Paid Ads",
    providers: ["linkedin"],
    color: "#0A66C2",
    metrics: [
      { key: "impressions", label: "Impressions" },
      { key: "clicks", label: "Clicks" },
      { key: "spend", label: "Spend" },
      { key: "conversions", label: "Conversions" },
      { key: "leads", label: "Leads" },
      { key: "ctr", label: "CTR (%)" },
    ],
  },
  {
    label: "TikTok Ads",
    segment: "Paid Ads",
    providers: ["tiktok"],
    color: "#010101",
    metrics: [
      { key: "impressions", label: "Impressions" },
      { key: "clicks", label: "Clicks" },
      { key: "spend", label: "Spend" },
      { key: "conversions", label: "Conversions" },
      { key: "video_views", label: "Video views" },
      { key: "ctr", label: "CTR (%)" },
    ],
  },
];

const SOURCE_GROUPS = [
  { id: "ga4", label: "Google Analytics", subtitle: "GA4 properties", providers: ["google"] },
  { id: "meta", label: "Meta Network", subtitle: "Facebook & Instagram", providers: ["meta"] },
  { id: "gsc", label: "Google Search Console", subtitle: "Organic search", providers: ["search_console"] },
  { id: "gads", label: "Google Ads", subtitle: "Paid search & display", providers: ["google_ads"] },
  { id: "linkedin", label: "LinkedIn", subtitle: "LinkedIn Ads", providers: ["linkedin"] },
  { id: "tiktok", label: "TikTok", subtitle: "TikTok Ads", providers: ["tiktok"] },
];

const DATE_PRESETS = [
  { label: "Last 7 days", relativeDays: 7 },
  { label: "Last 30 days", relativeDays: 30 },
  { label: "Last 90 days", relativeDays: 90 },
  { label: "Custom range", relativeDays: null },
];

const GROUP_BY_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const CHART_COLORS = [
  "#6366F1", "#0EA5E9", "#64748B", "#10B981", "#818CF8",
  "#94A3B8", "#06B6D4", "#8B5CF6",
];

const BRAND_BADGE = {
  google: { label: "GA4", className: "bg-red-50 text-red-700 ring-red-100" },
  meta: { label: "Meta", className: "bg-blue-50 text-blue-700 ring-blue-100" },
  search_console: { label: "GSC", className: "bg-emerald-50 text-emerald-700 ring-emerald-100" },
  google_ads: { label: "Ads", className: "bg-amber-50 text-amber-700 ring-amber-100" },
  linkedin: { label: "LI", className: "bg-blue-50 text-blue-800 ring-blue-100" },
  tiktok: { label: "TT", className: "bg-slate-100 text-slate-800 ring-slate-200" },
};

function fmt(value) {
  const n = Number(value || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}


function accountKindLabel(account) {
  const kind = account.providerAccountKind;
  if (kind === "instagram_business") return "Instagram";
  if (kind === "facebook_page") return "FB Page";
  if (kind === "facebook_ads_account") return "Meta Ads";
  if (kind === "facebook_catalog") return "Meta Catalog";
  if (kind === "ga4_property") return "GA4";
  return account.provider;
}

function Toast({ snackbar, onClose }) {
  if (!snackbar) return null;
  const styles = {
    success: "bg-emerald-600",
    warning: "bg-amber-500",
    error: "bg-red-600",
    info: "bg-indigo-600",
  };
  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div
        className={cn(
          "flex max-w-sm items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium text-white shadow-lg",
          styles[snackbar.severity] || styles.info
        )}
      >
        <span className="flex-1">{snackbar.message}</span>
        <button type="button" onClick={onClose} className="rounded p-0.5 hover:bg-white/20">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function SourceAccordionGroup({
  group,
  accounts,
  selectedIds,
  expanded,
  onToggleExpand,
  onToggleAccount,
}) {
  if (accounts.length === 0) return null;

  const selectedInGroup = accounts.filter((a) => selectedIds.includes(a.id)).length;

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        type="button"
        onClick={() => onToggleExpand(group.id)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left transition hover:bg-slate-50"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800">{group.label}</p>
          <p className="text-xs text-slate-400">{group.subtitle}</p>
        </div>
        <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
          {selectedInGroup}/{accounts.length}
        </span>
      </button>

      {expanded && (
        <div className="space-y-0.5 px-3 pb-3">
          {accounts.map((account) => {
            const checked = selectedIds.includes(account.id);
            const badge = BRAND_BADGE[account.provider] || BRAND_BADGE.meta;
            return (
              <label
                key={account.id}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 transition",
                  checked ? "bg-indigo-50 ring-1 ring-indigo-100" : "hover:bg-slate-50"
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => onToggleAccount(account.id, e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span
                      className={cn(
                        "inline-flex rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1",
                        badge.className
                      )}
                    >
                      {badge.label}
                    </span>
                    <AccountLabel
                      account={account}
                      nameClassName="text-sm text-slate-800"
                      idClassName="text-[10px]"
                    />
                    <AccountIdBadge account={account} />
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400">{accountKindLabel(account)}</p>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MetricCheckbox({ metric, checked, onChange, color }) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition",
        checked ? "bg-indigo-50/80" : "hover:bg-slate-50"
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(metric.key, e.target.checked)}
        className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
      />
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="text-sm text-slate-700">{metric.label}</span>
    </label>
  );
}

function MetricTotalCard({ metricKey, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card transition hover:shadow-card-hover">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{metricKey}</p>
      <p className="mt-1 text-2xl font-bold tracking-tight text-slate-900">{fmt(value)}</p>
    </div>
  );
}

export default function ReportsView() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [connected, setConnected] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [result, setResult] = useState(null);
  const [reportName, setReportName] = useState("");

  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [datePreset, setDatePreset] = useState(30);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [groupBy, setGroupBy] = useState("day");
  const [metricSearch, setMetricSearch] = useState("");
  const [dateMenuOpen, setDateMenuOpen] = useState(false);
  const [savedMenuOpen, setSavedMenuOpen] = useState(false);
  const [sourceNotes, setSourceNotes] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(() =>
    SOURCE_GROUPS.reduce((acc, g) => ({ ...acc, [g.id]: true }), {})
  );

  const isCustomDate = datePreset === null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [me, connectedList, reports] = await Promise.all([
        ApiService.getAccountMe().catch(() => ({ account: null })),
        IntegrationsService.listConnected({ includePaused: false }),
        ReportService.listReports().catch(() => []),
      ]);
      setAccount(me?.account ?? null);
      setConnected(Array.isArray(connectedList) ? connectedList : []);
      setSavedReports(Array.isArray(reports) ? reports : []);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/signin");
        return;
      }
      setError(e?.response?.data?.message || e.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!snackbar) return undefined;
    const t = setTimeout(() => setSnackbar(null), 4000);
    return () => clearTimeout(t);
  }, [snackbar]);

  function toggleAccount(id, checked) {
    setSelectedAccounts((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  }

  function toggleMetric(key, checked) {
    setSelectedMetrics((prev) => (checked ? [...prev, key] : prev.filter((x) => x !== key)));
  }

  function toggleGroupExpand(id) {
    setExpandedGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  async function handleRun() {
    if (selectedAccounts.length === 0) {
      setSnackbar({ severity: "warning", message: "Select at least one data source." });
      return;
    }
    if (selectedMetrics.length === 0) {
      setSnackbar({ severity: "warning", message: "Select at least one metric." });
      return;
    }
    setRunning(true);
    setError(null);
    try {
      const params = {
        connectedAccountIds: selectedAccounts,
        metrics: selectedMetrics,
        ...(isCustomDate ? { dateFrom, dateTo } : { relativeDays: datePreset }),
      };
      const data = await ReportService.runReport(params);
      setResult(data);
      setSourceNotes(Array.isArray(data?.disconnectedSources) ? data.disconnectedSources : []);
      if (!data.hasData) {
        setSnackbar({ severity: "info", message: "No data found. Sync your accounts first." });
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Report failed.");
    } finally {
      setRunning(false);
    }
  }

  async function handleSave() {
    if (!reportName.trim()) {
      setSnackbar({ severity: "warning", message: "Enter a report name first." });
      return;
    }
    setSaving(true);
    try {
      const params = {
        name: reportName.trim(),
        connectedAccountIds: selectedAccounts,
        metrics: selectedMetrics,
        ...(isCustomDate ? { dateFrom, dateTo } : { relativeDays: datePreset }),
      };
      await ReportService.saveReport(params);
      setSnackbar({ severity: "success", message: "Report saved." });
      await load();
    } catch (e) {
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Save failed." });
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteReport(id) {
    try {
      await ReportService.deleteReport(id);
      setSavedReports((prev) => prev.filter((r) => r.id !== id));
    } catch {
      setSnackbar({ severity: "error", message: "Delete failed." });
    }
  }

  function handleLoadReport(report) {
    setSelectedAccounts(report.connectedAccountIds || []);
    setSelectedMetrics(report.metrics || []);
    if (report.relativeDays) {
      setDatePreset(report.relativeDays);
    } else {
      setDatePreset(null);
      setDateFrom(report.dateFrom || "");
      setDateTo(report.dateTo || "");
    }
    setReportName(report.name || "");
    setResult(null);
    setSourceNotes(Array.isArray(report.disconnectedSources) ? report.disconnectedSources : []);
    setSavedMenuOpen(false);
  }

  async function handleSignOut() {
    try {
      await ApiService.signout();
    } catch {
      /* ignore */
    }
    navigate("/signin");
  }

  const chartSeries = useMemo(() => {
    if (!result) return [];
    return selectedMetrics
      .filter((m) => result.series?.[m]?.length > 0)
      .map((m, i) => ({
        metricKey: m,
        points: result.series[m],
        color: CHART_COLORS[i % CHART_COLORS.length],
      }));
  }, [result, selectedMetrics]);

  const chartXDates = useMemo(() => {
    if (chartSeries.length === 0) return [];
    const all = new Set();
    chartSeries.forEach((s) => s.points.forEach((p) => all.add(p.date)));
    return [...all].sort().map((d) => new Date(d));
  }, [chartSeries]);

  const chartMuiSeries = useMemo(() => {
    return chartSeries.map((s) => {
      const dateMap = {};
      s.points.forEach((p) => {
        dateMap[p.date] = p.value;
      });
      const yData = chartXDates.map((d) => {
        const key = d.toISOString().slice(0, 10);
        return Number(dateMap[key] || 0);
      });
      return { data: yData, label: s.metricKey, color: s.color, area: false, curve: "monotoneX" };
    });
  }, [chartSeries, chartXDates]);

  const tableRows = useMemo(() => {
    if (!result) return [];
    const dateSet = new Set();
    selectedMetrics.forEach((m) => {
      (result.series?.[m] || []).forEach((p) => dateSet.add(p.date));
    });
    const dates = [...dateSet].sort();
    return dates.map((date) => {
      const row = { date };
      selectedMetrics.forEach((m) => {
        const point = (result.series?.[m] || []).find((p) => p.date === date);
        row[m] = point ? Number(point.value) : 0;
      });
      return row;
    });
  }, [result, selectedMetrics]);

  const hasNoConnections = !loading && connected.length === 0;

  const selectedAccountMeta = useMemo(() => {
    const ids = new Set(selectedAccounts);
    return connected
      .filter((c) => ids.has(c.id))
      .map((c) => ({ provider: c.provider, kind: c.providerAccountKind }));
  }, [selectedAccounts, connected]);

  const visibleGroups = useMemo(() => {
    if (selectedAccountMeta.length === 0) return METRIC_GROUPS;
    return METRIC_GROUPS.filter((g) =>
      selectedAccountMeta.some(({ provider, kind }) => {
        if (!g.providers.includes(provider)) return false;
        if (g.kinds && !g.kinds.includes(kind)) return false;
        if (g.excludeKinds && g.excludeKinds.includes(kind)) return false;
        return true;
      })
    );
  }, [selectedAccountMeta]);

  const filteredMetricSections = useMemo(() => {
    const q = metricSearch.trim().toLowerCase();
    const groups = visibleGroups.map((group) => ({
      ...group,
      metrics: q
        ? group.metrics.filter(
            (m) =>
              m.label.toLowerCase().includes(q) || m.key.toLowerCase().includes(q)
          )
        : group.metrics,
    })).filter((g) => g.metrics.length > 0);

    const segments = ["Paid Ads", "Organic Social", "Web Traffic"];
    return segments
      .map((segment) => ({
        segment,
        groups: groups.filter((g) => g.segment === segment),
      }))
      .filter((s) => s.groups.length > 0);
  }, [visibleGroups, metricSearch]);

  const canRun = selectedAccounts.length > 0 && selectedMetrics.length > 0;

  const activeDateLabel = useMemo(() => {
    if (isCustomDate) {
      if (dateFrom && dateTo) return `${dateFrom} → ${dateTo}`;
      return "Custom range";
    }
    return DATE_PRESETS.find((p) => p.relativeDays === datePreset)?.label || "Last 30 days";
  }, [isCustomDate, dateFrom, dateTo, datePreset]);

  const accountsBySourceGroup = useMemo(() => {
    return SOURCE_GROUPS.map((group) => ({
      group,
      accounts: connected.filter((c) => group.providers.includes(c.provider)),
    }));
  }, [connected]);

  if (hasNoConnections) {
    return (
      <AppShell account={account} onSignOut={handleSignOut} title="Report builder">
        <div className="mx-auto flex max-w-lg flex-col items-center px-6 py-20 text-center">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-100">
            <Plug className="h-8 w-8 text-indigo-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900">No data sources connected</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            Connect Facebook, Instagram, or Google Analytics to start building custom reports.
          </p>
          <RouterLink
            to="/integrations"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
          >
            Connect data sources
            <ArrowRight className="h-4 w-4" />
          </RouterLink>
        </div>
        <Toast snackbar={snackbar} onClose={() => setSnackbar(null)} />
      </AppShell>
    );
  }

  return (
    <AppShell account={account} onSignOut={handleSignOut} title="Report builder" workspace>
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Column 1 — Data Sources (20%) */}
        <aside className="flex w-full shrink-0 flex-col border-b border-slate-200 bg-white lg:w-[20%] lg:border-b-0 lg:border-r">
          <div className="border-b border-slate-100 px-4 py-4">
            <h2 className="text-sm font-semibold text-slate-900">Data Sources</h2>
            <p className="mt-0.5 text-xs text-slate-400">
              {selectedAccounts.filter((id) => connected.some((c) => c.id === id)).length} of {connected.length} selected
            </p>
          </div>
          {sourceNotes.length > 0 && (
            <div className="border-b border-amber-100 bg-amber-50 px-4 py-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5" />
                source disconnected
              </p>
              <ul className="mt-1.5 space-y-1">
                {sourceNotes.map((note) => (
                  <li key={note.id} className="truncate text-xs text-amber-900">
                    {note.displayName || "A saved source"} — source disconnected
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {loading ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />
                ))}
              </div>
            ) : (
              accountsBySourceGroup.map(({ group, accounts }) => (
                <SourceAccordionGroup
                  key={group.id}
                  group={group}
                  accounts={accounts}
                  selectedIds={selectedAccounts}
                  expanded={expandedGroups[group.id]}
                  onToggleExpand={toggleGroupExpand}
                  onToggleAccount={toggleAccount}
                />
              ))
            )}
          </div>
        </aside>

        {/* Column 2 — Metrics & Dimensions (25%) */}
        <aside className="flex w-full shrink-0 flex-col border-b border-slate-200 bg-white lg:w-[25%] lg:border-b-0 lg:border-r">
          <div className="border-b border-slate-100 px-4 py-4">
            <h2 className="text-sm font-semibold text-slate-900">Metrics & Dimensions</h2>
            <p className="mt-0.5 text-xs text-slate-400">
              {selectedMetrics.length} metric{selectedMetrics.length !== 1 ? "s" : ""} selected
            </p>
            <div className="relative mt-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                placeholder="Search metrics…"
                value={metricSearch}
                onChange={(e) => setMetricSearch(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 transition focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
            {filteredMetricSections.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-slate-400">No metrics match your search.</p>
            ) : (
              filteredMetricSections.map(({ segment, groups }) => (
                <div key={segment} className="mb-5">
                  <p className="mb-2 px-2 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">
                    {segment}
                  </p>
                  <div className="space-y-3">
                    {groups.map((group) => (
                      <div
                        key={group.label}
                        className="rounded-xl border border-slate-100 bg-slate-50/50 p-2"
                      >
                        <div className="mb-1.5 flex items-center gap-2 px-2">
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: group.color }}
                          />
                          <span className="text-xs font-semibold text-slate-600">{group.label}</span>
                        </div>
                        <div className="space-y-0.5">
                          {group.metrics.map((m) => (
                            <MetricCheckbox
                              key={m.key}
                              metric={m}
                              color={group.color}
                              checked={selectedMetrics.includes(m.key)}
                              onChange={toggleMetric}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Save report — compact footer */}
          <div className="border-t border-slate-100 p-4">
            <input
              type="text"
              placeholder="Report name…"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              className="mb-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving…" : "Save report"}
            </button>
          </div>
        </aside>

        {/* Column 3 — Preview Canvas */}
        <section className="flex min-h-0 min-w-0 flex-1 flex-col bg-canvas">
          {/* Top control bar */}
          <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 lg:px-6">
            {/* Date range picker */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setDateMenuOpen((v) => !v)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300"
              >
                <Calendar className="h-4 w-4 text-slate-400" />
                {activeDateLabel}
                <ChevronDown className={cn("h-4 w-4 text-slate-400 transition", dateMenuOpen && "rotate-180")} />
              </button>
              {dateMenuOpen && (
                <>
                  <button
                    type="button"
                    aria-label="Close date menu"
                    className="fixed inset-0 z-30"
                    onClick={() => setDateMenuOpen(false)}
                  />
                  <div className="absolute left-0 z-40 mt-1 w-72 rounded-xl border border-slate-200 bg-white p-3 shadow-lg">
                    <div className="space-y-1">
                      {DATE_PRESETS.map((p) => (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => {
                            setDatePreset(p.relativeDays);
                            if (p.relativeDays !== null) setDateMenuOpen(false);
                          }}
                          className={cn(
                            "w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition",
                            datePreset === p.relativeDays
                              ? "bg-indigo-50 text-indigo-700"
                              : "text-slate-600 hover:bg-slate-50"
                          )}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    {isCustomDate && (
                      <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                        <label className="block text-xs font-medium text-slate-500">
                          From
                          <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          />
                        </label>
                        <label className="block text-xs font-medium text-slate-500">
                          To
                          <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => setDateMenuOpen(false)}
                          className="w-full rounded-lg bg-slate-100 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200"
                        >
                          Apply
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Group by */}
            <div className="relative flex items-center gap-2">
              <Layers className="h-4 w-4 text-slate-400" />
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="appearance-none rounded-lg border border-slate-200 bg-white py-2 pl-3 pr-8 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              >
                {GROUP_BY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    Group by {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1" />

            {/* Saved reports */}
            {savedReports.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setSavedMenuOpen((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:border-slate-300"
                >
                  <Bookmark className="h-4 w-4 text-slate-400" />
                  Saved
                  <ChevronDown className={cn("h-4 w-4 text-slate-400", savedMenuOpen && "rotate-180")} />
                </button>
                {savedMenuOpen && (
                  <>
                    <button
                      type="button"
                      aria-label="Close saved menu"
                      className="fixed inset-0 z-30"
                      onClick={() => setSavedMenuOpen(false)}
                    />
                    <div className="absolute right-0 z-40 mt-1 w-80 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
                      {savedReports.map((r) => (
                        <div
                          key={r.id}
                          className="flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-slate-50"
                        >
                          <button
                            type="button"
                            onClick={() => handleLoadReport(r)}
                            className="min-w-0 flex-1 text-left"
                          >
                            <p className="truncate text-sm font-semibold text-slate-800">{r.name}</p>
                            {(r.disconnectedSources || []).length > 0 && (
                              <p className="truncate text-xs font-medium text-amber-700">source disconnected</p>
                            )}
                            <p className="truncate text-xs text-slate-400">
                              {(r.metrics || []).slice(0, 2).join(", ")}
                              {(r.metrics || []).length > 2 ? ` +${(r.metrics || []).length - 2}` : ""}
                            </p>
                          </button>
                          <button
                            type="button"
                            title="Load"
                            onClick={() => handleLoadReport(r)}
                            className="rounded-lg p-1.5 text-indigo-600 hover:bg-indigo-50"
                          >
                            <FolderOpen className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={() => handleDeleteReport(r.id)}
                            className="rounded-lg p-1.5 text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Run report */}
            <button
              type="button"
              onClick={handleRun}
              disabled={running || !canRun}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4 fill-current" />
              )}
              {running ? "Running…" : "Run report"}
            </button>
          </div>

          {error && (
            <div className="mx-4 mt-4 flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 lg:mx-6">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Canvas body */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4 lg:p-6">
            {!result ? (
              <div className="flex h-full min-h-[420px] items-center justify-center">
                <div className="w-full max-w-md rounded-2xl border border-dashed border-slate-200 bg-white/80 px-8 py-14 text-center shadow-sm">
                  <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50">
                    <SlidersHorizontal className="h-7 w-7 text-indigo-500" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    Configure and run a report
                  </h3>
                  <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-slate-500">
                    Select data sources and metrics in the panels on the left, choose a date range
                    above, then hit{" "}
                    <span className="font-semibold text-indigo-600">Run report</span>.
                  </p>
                  {!canRun && (
                    <p className="mt-4 text-xs text-slate-400">
                      Select at least one source and one metric to continue.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {(result.disconnectedSources || []).length > 0 && (
                  <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <div>
                      <p className="font-semibold">source disconnected</p>
                      <ul className="mt-1 space-y-0.5 text-xs">
                        {(result.disconnectedSources || []).map((note) => (
                          <li key={note.id}>
                            {note.displayName || "A saved source"} was skipped — source disconnected
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {/* KPI totals */}
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
                  {selectedMetrics.map((m) => (
                    <MetricTotalCard key={m} metricKey={m} value={result.totals?.[m]} />
                  ))}
                </div>

                {/* Chart */}
                {chartXDates.length > 0 && chartMuiSeries.length > 0 && (
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="h-4 w-4 text-indigo-500" />
                        <h3 className="font-semibold text-slate-900">Trend</h3>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {result.dateFrom} – {result.dateTo} · grouped by {groupBy} ·{" "}
                        {chartMuiSeries.length} metric{chartMuiSeries.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="px-3 pb-4 pt-2">
                      <LineChart
                        height={320}
                        xAxis={[{ data: chartXDates, scaleType: "time" }]}
                        series={chartMuiSeries}
                        margin={{ left: 56, right: 16, top: 16, bottom: 40 }}
                        sx={{ ".MuiLineElement-root": { strokeWidth: 2 } }}
                      />
                    </div>
                  </div>
                )}

                {/* Data table */}
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-5 py-4">
                    <div>
                      <h3 className="font-semibold text-slate-900">Data table</h3>
                      <p className="text-xs text-slate-500">
                        {tableRows.length} rows · {result.dateFrom} – {result.dateTo}
                      </p>
                    </div>
                    <ExportButton
                      rows={tableRows}
                      filename={`report-${result.dateFrom}-${result.dateTo}.csv`}
                      columns={[
                        { label: "Date", value: (r) => r.date },
                        ...selectedMetrics.map((m) => ({ label: m, value: (r) => r[m] })),
                      ]}
                    />
                  </div>
                  <div className="overflow-x-auto">
                    {tableRows.length === 0 ? (
                      <p className="px-5 py-12 text-center text-sm text-slate-500">
                        No data. Sync your connected accounts first, then re-run.
                      </p>
                    ) : (
                      <table className="w-full min-w-[480px] text-left">
                        <thead>
                          <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-400">
                            <th className="px-5 py-3">Date</th>
                            {selectedMetrics.map((m) => (
                              <th key={m} className="px-4 py-3 text-right">
                                {m}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {tableRows.map((row) => (
                            <tr key={row.date} className="transition hover:bg-slate-50/60">
                              <td className="px-5 py-3 text-sm font-medium text-slate-800">
                                {row.date}
                              </td>
                              {selectedMetrics.map((m) => (
                                <td
                                  key={m}
                                  className="px-4 py-3 text-right text-sm font-semibold tabular-nums text-slate-700"
                                >
                                  {fmt(row[m])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      <Toast snackbar={snackbar} onClose={() => setSnackbar(null)} />
    </AppShell>
  );
}
